<template>
  <div class="flex flex-col bg-white rounded-xl w-full">
    <div
      class="flex flex-col gap-4 pt-6 pb-4 px-6 border-b border-slate-100"
      style="box-shadow: 0px 1px 4px 0px rgba(202, 202, 202, 0.16)"
    >
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between w-full">
          <div class="flex items-center gap-2 flex-1 min-w-0">
            <v2-icon icon="add" class="text-gray-500" />
            <h2 class="text-lg font-semibold text-gray-800 leading-5 m-0">
              {{ $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.TITLE') }}
            </h2>
          </div>
        </div>
        <p class="text-sm text-gray-600 leading-5 !mt-0">
          {{ $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <div class="flex flex-col gap-3 px-6 py-4">
      <div class="flex flex-col gap-1.5">
        <label class="text-sm leading-5 text-gray-800">{{
          $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.SELECT_LABEL')
        }}</label>
        <div class="relative">
          <select
            v-model="selectedListId"
            class="w-full h-11 px-3 pe-11 rounded-lg border border-gray-300 bg-white text-base leading-6 text-gray-700 appearance-none outline-none hover:border-gray-400 focus:border-gray-400 transition-colors"
            :disabled="uiFlags.isFetching"
          >
            <option value="" disabled>
              {{ $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.SELECT_PLACEHOLDER') }}
            </option>
            <option v-for="list in userLists" :key="list.id" :value="list.id">
              {{ list.name }}
            </option>
          </select>
          <v2-icon
            icon="keyboard_arrow_down"
            class="pointer-events-none absolute top-1/2 -translate-y-1/2 end-3 text-gray-500"
          />
        </div>
      </div>

      <div class="flex items-center gap-3">
        <div class="flex-1 h-px bg-gray-200" />
        <span class="text-sm leading-5 text-gray-300">{{
          $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.OR')
        }}</span>
        <div class="flex-1 h-px bg-gray-200" />
      </div>

      <button-v2
        variant="outline"
        size="lg"
        icon-left="list_add"
        full-width
        @click="showCreateList = true"
      >
        {{ $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.CREATE_NEW_LIST') }}
      </button-v2>

      <div
        v-if="showCreateList"
        class="flex flex-col gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200"
      >
        <label class="text-sm text-slate-700">{{
          $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.LIST_NAME')
        }}</label>
        <input
          v-model.trim="newListName"
          type="text"
          :placeholder="
            $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.LIST_NAME_PLACEHOLDER')
          "
          class="h-11 w-full px-3 rounded-lg border border-slate-300 bg-white text-base text-slate-900 outline-none focus:ring-2 focus:ring-brand transition-colors"
          @keydown.enter.prevent="createAndAdd"
        />
        <div class="flex items-center justify-end gap-2 mt-1">
          <button
            type="button"
            class="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded transition-colors"
            @click="
              showCreateList = false;
              newListName = '';
            "
          >
            {{ $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.CANCEL') }}
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm rounded transition-colors"
            :disabled="!newListName || uiFlags.isCreating || !canCreateAndAdd"
            :title="canCreateAndAdd ? null : disabledActionHint"
            :class="
              newListName && canCreateAndAdd
                ? 'bg-brand text-white hover:bg-brand-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            "
            @click="createAndAdd"
          >
            {{
              uiFlags.isCreating
                ? $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.CREATING')
                : $t('CONTACTS_PAGE.ADD_TO_LIST_MODAL.CREATE_AND_ADD')
            }}
          </button>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-end gap-3 pt-4 pb-6 px-6 border-t border-slate-100"
      style="box-shadow: 0px -1px 4px 0px rgba(202, 202, 202, 0.16)"
    >
      <button-v2
        variant="naked"
        size="lg"
        class="cancel-filter-btn"
        @click="$emit('close')"
      >
        {{ $t('CONTACTS_FILTER.CANCEL_BUTTON_LABEL') }}
      </button-v2>
      <button-v2
        variant="filled"
        size="lg"
        :disabled="
          !selectedListId || uiFlags.isAddingMember || !canManageListMembers
        "
        :title="canManageListMembers ? null : disabledActionHint"
        class="apply-filter-btn"
        @click="addToList"
      >
        {{ $t('CONTACTS_FILTER.ADD_BUTTON_LABEL') }}
      </button-v2>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import listPermissionsMixin from 'v2/helpers/listPermissions';
import {
  addMemberErrorMessage,
  skippedMemberMessage,
} from 'v2/helpers/listErrors';
import ButtonV2 from 'v2/components/Button/ButtonV2.vue';
import V2Icon from 'v2/components/Icon.vue';

export default {
  components: { ButtonV2, V2Icon },
  mixins: [alertMixin, listPermissionsMixin],
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      selectedListId: '',
      showCreateList: false,
      newListName: '',
    };
  },
  computed: {
    ...mapGetters({
      lists: 'lists/getLists',
      uiFlags: 'lists/getUIFlags',
    }),
    userLists() {
      return this.lists.filter(list => !list.system_list);
    },
    canCreateAndAdd() {
      return this.canCreateList && this.canManageListMembers;
    },
  },
  mounted() {
    this.$store.dispatch('lists/get');
  },
  methods: {
    async addToList() {
      if (!this.selectedListId || !this.canManageListMembers) return;
      try {
        const result = await this.$store.dispatch('lists/addMember', {
          listId: this.selectedListId,
          contactId: this.contactId,
        });
        this.notifyAddResult(result);
        this.$emit('close');
      } catch (error) {
        this.showAlert(this.addErrorMessage(error));
      }
    },
    notifyAddResult(result) {
      const skipped = skippedMemberMessage(result, key => this.$t(key));
      this.showAlert(skipped || this.$t('LISTS_PAGE.ADD_CONTACT.SUCCESS'));
    },
    addErrorMessage(error) {
      return (
        addMemberErrorMessage(error, key => this.$t(key)) ||
        this.$t('LISTS_PAGE.ADD_CONTACT.ERROR')
      );
    },
    async createAndAdd() {
      if (!this.newListName || !this.canCreateAndAdd) return;
      try {
        const list = await this.$store.dispatch('lists/create', {
          name: this.newListName,
        });
        const result = await this.$store.dispatch('lists/addMember', {
          listId: list.id,
          contactId: this.contactId,
        });
        this.notifyAddResult(result);
        this.$emit('close');
      } catch (error) {
        this.showAlert(this.addErrorMessage(error));
      }
    },
  },
};
</script>
